import { isApiError, unwrap } from "@sagot/api-client";
import { type PresenceStatus } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { BEAT_CHECK_MS, HEARTBEAT_MS, beatDue } from "./presence-model";

/** The rollout changes rarely and a super admin flips it from the admin console: five minutes is fresh enough. */
const LIVE_CHAT_STATE_STALE_MS = 5 * 60_000;

export const presenceKeys = {
  state: (ws: string) => ["ws", ws, "routing", "live-chat"] as const,
  me: (ws: string) => ["ws", ws, "routing", "presence", "me"] as const,
  list: (ws: string) => ["ws", ws, "routing", "presence", "list"] as const,
  limits: (ws: string) => ["ws", ws, "routing", "chat-limits"] as const,
};

/** The platform flag `live_chat_ops` is off for the workspace: the API answers 404 `not_found` and the console hides the entry. */
export function isLiveChatOff(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}

/**
 * `GET /v1/routing/live-chat`: whether the workspace is in the `live_chat_ops` rollout. It always answers 200, so it is asked before
 * any flagged route: a workspace outside the rollout never requests one and the console logs no 404 (BUG-M29-LAUNCHCON-001).
 * `useLiveChatState(false)` does not ask (the member could not use the answer).
 */
export function useLiveChatState(enabled: boolean) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: presenceKeys.state(ws),
    queryFn: () => unwrap(api.GET("/v1/routing/live-chat")),
    enabled,
    retry: 1,
    staleTime: LIVE_CHAT_STATE_STALE_MS,
  });
}

/** `GET /v1/routing/presence/me`: what the member's status reads as, the chats they hold and their limit. Polled once a minute so an automatic away shows. */
export function useMyPresence(enabled: boolean) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: presenceKeys.me(ws),
    queryFn: () => unwrap(api.GET("/v1/routing/presence/me")),
    enabled,
    retry: false,
    staleTime: 30_000,
    refetchInterval: HEARTBEAT_MS,
  });
}

/** `PUT /v1/routing/presence/me`: a pick and a heartbeat are the same call; the answer is the member's row. */
export function useSetMyPresence() {
  const api = useApi();
  const ws = useWorkspaceId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: PresenceStatus) => unwrap(api.PUT("/v1/routing/presence/me", { body: { status } })),
    onSuccess: async (row) => {
      queryClient.setQueryData(presenceKeys.me(ws), row);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: presenceKeys.list(ws) }),
        queryClient.invalidateQueries({ queryKey: presenceKeys.limits(ws) }),
      ]);
    },
  });
}

const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "focus"] as const;

interface BeatTarget {
  status: PresenceStatus;
  auto_away: boolean;
}

/**
 * Keeps the member's presence alive while they use the console: an activity listener plus a five-second check that sends the
 * `online` heartbeat when `beatDue` says so. A reload counts as activity, so a member who was online stays online after it.
 */
export function usePresenceHeartbeat(me: BeatTarget | undefined, send: (status: PresenceStatus) => void): void {
  const lastActivityAt = useRef(0);
  const lastBeatAt = useRef(0);
  const latest = useRef({ me, send });
  useEffect(() => {
    latest.current = { me, send };
  });
  useEffect(() => {
    lastActivityAt.current = Date.now();
    const mark = () => { lastActivityAt.current = Date.now(); };
    for (const name of ACTIVITY_EVENTS) window.addEventListener(name, mark, { passive: true });
    const check = () => {
      const { me: current, send: dispatch } = latest.current;
      if (!current) return;
      const now = Date.now();
      const due = beatDue({ status: current.status, autoAway: current.auto_away, lastActivityAt: lastActivityAt.current, lastBeatAt: lastBeatAt.current, now });
      if (!due) return;
      lastBeatAt.current = now;
      dispatch("online");
    };
    const timer = window.setInterval(check, BEAT_CHECK_MS);
    check();
    return () => {
      window.clearInterval(timer);
      for (const name of ACTIVITY_EVENTS) window.removeEventListener(name, mark);
    };
  }, []);
}
